// O que exatamente para de funcionar se este certificado vencer.
//
// A contagem sozinha não responde a pergunta operacional: "3 configurações" não
// diz se são três métodos do mesmo white label ou três lojistas diferentes — e é
// isso que decide se a substituição pode esperar.
import { ExternalLinkIcon } from "lucide-react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { Badge } from "@/components/ui/badge"
import { FRONTEND_ROUTES } from "@/constants"
import type { MerchantMtlsCertificateLinkedConfig } from "@/types/admin-merchant-mtls-certificate"

const panelPrefix = "pages.adminMerchants"

type MtlsCertificateLinkedConfigsProps = {
  configs: MerchantMtlsCertificateLinkedConfig[]
  /** Vínculos sem configuração — a diferença entre a contagem e esta lista. */
  orphanProviders: number
}

export const MtlsCertificateLinkedConfigs = ({
  configs,
  orphanProviders,
}: MtlsCertificateLinkedConfigsProps) => {
  const { t } = useTranslation()

  if (configs.length === 0 && orphanProviders === 0) {
    return (
      <p className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
        {t(
          `${panelPrefix}.mtlsCertificates.linkedConfigsEmpty`
        )}
      </p>
    )
  }

  return (
    <div className="space-y-2">
      <ul className="space-y-2">
        {configs.map((config) => (
          <li
            key={config.configId}
            className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3"
          >
            <div className="min-w-0 space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium">
                  {config.gateway ?? "—"}
                </span>
                <span
                  className="text-sm text-muted-foreground"
                  aria-hidden="true"
                >
                  ·
                </span>
                <span className="text-sm">
                  {config.method ?? "—"}
                </span>
                <Badge
                  variant="outline"
                  className={
                    config.active
                      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                      : "border-gray-200 bg-gray-50 text-gray-700"
                  }
                >
                  {t(
                    `${panelPrefix}.mtlsCertificates.${config.active ? "configActive" : "configInactive"}`
                  )}
                </Badge>
                {config.fallbackOnly && (
                  // Só na contingência: o caminho principal usa outro material,
                  // então este certificado não derruba o método sozinho.
                  <Badge
                    variant="outline"
                    className="border-amber-200 bg-amber-50 text-amber-700"
                  >
                    {t(
                      `${panelPrefix}.mtlsCertificates.configFallback`
                    )}
                  </Badge>
                )}
              </div>
              <p className="break-all font-mono text-xs text-muted-foreground">
                {config.whiteLabelId ?? "—"}
              </p>
            </div>
            {/* O painel de processadores filtra por `whiteLabel`; é o deep-link
                que existe, e cai no grupo da configuração. */}
            <Link
              to={`${FRONTEND_ROUTES.ADMIN_PROCESSOR_CONFIG.href}?whiteLabel=${config.whiteLabelId ?? ""}`}
              className="inline-flex items-center gap-1 text-sm font-medium text-primary underline-offset-4 hover:underline"
            >
              {t(
                `${panelPrefix}.mtlsCertificates.openConfig`
              )}
              <ExternalLinkIcon
                className="size-3.5"
                aria-hidden="true"
              />
            </Link>
          </li>
        ))}
      </ul>

      {orphanProviders > 0 && (
        // Sem isto o painel diria "3 configurações" e mostraria uma — a
        // diferença são providers de configurações já apagadas.
        <p className="text-xs text-muted-foreground">
          {t(
            `${panelPrefix}.mtlsCertificates.orphanProviders`,
            { count: orphanProviders }
          )}
        </p>
      )}
    </div>
  )
}
